import { Reply, CheckCircle, Circle, Trash2 } from 'lucide-react'
import { trackEvent } from '@/utils/analytics'
import { useState } from 'react'
import { toast } from 'sonner'
import type { Comment } from '@/types/comment'
import { useAppStore } from '@/store'
import { colors } from '@/styles/colors'
import TooltipOverlay from '@/components/TooltipOverlay'
import { useTranslation } from '@/i18n'

interface CommentActionsProps {
  comment: Comment
  onReply: (commentId: string) => void
}

const CommentActions = ({ comment, onReply }: CommentActionsProps) => {
  const { t } = useTranslation()
  const { user, resolveComment, deleteComment } = useAppStore()
  const [isResolving, setIsResolving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const isOwnComment = comment.user_id === user?.userId

  const handleReply = () => {
    onReply(comment.id)
  }

  const handleResolve = async () => {
    if (isResolving) return

    setIsResolving(true)
    try {
      await resolveComment(comment.id, !comment.is_resolved)
      trackEvent('comment-resolved', { comment_id: comment.id, resolved: !comment.is_resolved })
      toast.success(
        comment.is_resolved ? t('common:comments.comment_unresolved') : t('common:comments.comment_resolved')
      )
    } catch (error) {
      toast.error(comment.is_resolved ? t('common:comments.failed_unresolve') : t('common:comments.failed_resolve'))
      console.error('Resolve comment error:', error)
    } finally {
      setIsResolving(false)
    }
  }

  const handleDelete = async () => {
    if (isDeleting) return

    if (!confirm(t('common:comments.delete_confirm'))) {
      return
    }

    setIsDeleting(true)
    try {
      await deleteComment(comment.id)
      trackEvent('comment-deleted', { comment_id: comment.id })
      toast.success(t('common:comments.comment_deleted'))
    } catch (error) {
      toast.error(t('common:comments.failed_delete'))
      console.error('Delete comment error:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="flex gap-3 items-center">
      <TooltipOverlay message={t('common:comments.tooltips.reply')}>
        <Reply
          size={17}
          className="text-primary cursor-pointer transition-all opacity-60 hover:opacity-100"
          onClick={handleReply}
        />
      </TooltipOverlay>

      <TooltipOverlay message={comment.is_resolved ? t('common:comments.tooltips.unresolve') : t('common:comments.tooltips.resolve')}>
        {comment.is_resolved ? (
          <CheckCircle
            size={17}
            className="text-primary cursor-pointer transition-all opacity-60 hover:opacity-100"
            onClick={handleResolve}
            style={{ opacity: isResolving ? 0.3 : undefined }}
          />
        ) : (
          <Circle
            size={17}
            className="text-primary cursor-pointer transition-all opacity-60 hover:opacity-100"
            onClick={handleResolve}
            style={{ opacity: isResolving ? 0.3 : undefined }}
          />
        )}
      </TooltipOverlay>

      {isOwnComment && (
        <TooltipOverlay message={t('common:comments.tooltips.delete')}>
          <Trash2
            size={17}
            className="cursor-pointer transition-all opacity-60 hover:opacity-100"
            style={{ color: colors.error[500], opacity: isDeleting ? 0.3 : undefined }}
            onClick={handleDelete}
          />
        </TooltipOverlay>
      )}
    </div>
  )
}

export default CommentActions
